type MutationListener = (count: number) => void;

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const listeners = new Set<MutationListener>();
const inFlightRequests = new Map<string, Promise<unknown>>();

let pendingMutations = 0;
let formSubmitLocked = false;

const notify = () => {
  listeners.forEach((listener) => listener(pendingMutations));
};

export const isMutationMethod = (method?: string) =>
  MUTATION_METHODS.has((method || 'get').toLowerCase());

export const getRequestKey = (config: {
  method?: string;
  url?: string;
  data?: unknown;
}) => {
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  let data = '';
  try {
    data =
      typeof config.data === 'string'
        ? config.data
        : JSON.stringify(config.data ?? '');
  } catch {
    data = '';
  }
  return `${method}|${url}|${data}`;
};

export const getPendingMutationCount = () => pendingMutations;

export const isFormSubmitLocked = () => formSubmitLocked;

export const subscribeMutationCount = (listener: MutationListener) => {
  listeners.add(listener);
  listener(pendingMutations);
  return () => {
    listeners.delete(listener);
  };
};

export const beginMutation = () => {
  pendingMutations += 1;
  notify();
};

export const endMutation = () => {
  pendingMutations = Math.max(0, pendingMutations - 1);
  if (pendingMutations === 0) {
    formSubmitLocked = false;
  }
  notify();
};

export const lockFormSubmit = () => {
  formSubmitLocked = true;
  notify();
};

export const unlockFormSubmit = () => {
  if (pendingMutations === 0) {
    formSubmitLocked = false;
    notify();
  }
};

export const getInFlightRequest = (key: string) => inFlightRequests.get(key);

export const setInFlightRequest = (key: string, promise: Promise<unknown>) => {
  inFlightRequests.set(key, promise);
};

export const clearInFlightRequest = (key: string) => {
  inFlightRequests.delete(key);
};

export const waitForMutationsIdle = () =>
  new Promise<void>((resolve) => {
    if (pendingMutations === 0) {
      resolve();
      return;
    }
    const unsubscribe = subscribeMutationCount((count) => {
      if (count === 0) {
        unsubscribe();
        resolve();
      }
    });
  });

export const awaitInFlightMutations = async () => {
    const deadline = Date.now() + 150;
  while (pendingMutations === 0 && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 20));
  }
  if (pendingMutations > 0) {
    await waitForMutationsIdle();
  }
};

export function protectSubmit<V>(
  fn: (values: V, helpers: any) => unknown | Promise<unknown>,
) {
  let inProgress = false;
  return async (values: V, helpers: any) => {
    if (inProgress || getPendingMutationCount() > 0) {
      helpers?.setSubmitting?.(false);
      return;
    }
    inProgress = true;
    lockFormSubmit();
    try {
      await fn(values, helpers);
      await awaitInFlightMutations();
    } catch {
      // Callers already toast/handle API errors; don't leave an unhandled rejection.
    } finally {
      inProgress = false;
      helpers?.setSubmitting?.(false);
      unlockFormSubmit();
    }
  };
}

const shouldBlockRepeatSubmit = () =>
  formSubmitLocked || pendingMutations > 0;

if (typeof document !== 'undefined') {
  document.addEventListener(
    'submit',
    (event: Event) => {
      if (shouldBlockRepeatSubmit()) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true,
  );
  document.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || !shouldBlockRepeatSubmit()) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        !target ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    true,
  );
}

